import {
  AnchorHTMLAttributes,
  FC,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { TextLink } from '../common/components/typography';
import { CsrfStore } from './CsrfTokenProvider';
import translations from './LogoutButton.translations.json';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

const { addTranslations, useTranslation } = i18nNsFactory('logout-button');
addTranslations(translations);

const LOGOUT_PATH = import.meta.env.LOGIN_PATH || '/backend/api/auth/logout';
const REDIRECT_URI_PARAM_NAME =
  import.meta.env.REDIRECT_URI_PARAM_NAME || 'clientRequestedUrl';

export interface LogoutLinkProps
  extends AnchorHTMLAttributes<HTMLAnchorElement> {
  id: string;
  redirectPath?: string;
}

export const LogoutLink: FC<PropsWithChildren<LogoutLinkProps>> = (props) => {
  const { redirectPath, ...linkProps } = props;
  const { t } = useTranslation();
  const { children = t('fallback') } = props;
  const formRef = useRef<HTMLFormElement>(null);

  const handleClick = useCallback(() => {
    formRef.current?.submit();
  }, [formRef]);

  return (
    <LogoutForm redirectPath={redirectPath} ref={formRef}>
      <TextLink {...linkProps} onClick={handleClick}>
        {children}
      </TextLink>
    </LogoutForm>
  );
};

interface LogoutFormProps extends PropsWithChildren {
  redirectPath?: string;
}

const LogoutForm = forwardRef<HTMLFormElement, LogoutFormProps>(
  (props, ref) => {
    const { children, redirectPath } = props;
    const [csrfToken] = CsrfStore.useValue('csrfToken');
    const { pathname, search } = useLocation();
    const redirectUri = redirectPath || pathname + search;

    const logoutPath = useMemo(
      () =>
        `${LOGOUT_PATH}?${new URLSearchParams({
          [REDIRECT_URI_PARAM_NAME]: redirectUri,
        })}`,
      [redirectUri],
    );

    return (
      <InlineForm action={logoutPath} method="POST" ref={ref}>
        <input type="hidden" name="_csrf" value={csrfToken} />
        {children}
      </InlineForm>
    );
  },
);

const InlineForm = styled.form`
  display: inline;
`;
